import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

import { type NarrativeUploadFile, NarrativeUploadFileCard } from './NarrativeUploadFileCard';

interface NarrativeUploadDialogProps {
  files: Array<NarrativeUploadFile>;
  onCancelFile?: (fileId: string) => void;
  onClose?: () => void;
  onOpenChange: (open: boolean) => void;
  onRetry?: () => void;
  onRetryFile?: (fileId: string) => void;
  open: boolean;
}

export function NarrativeUploadDialog({
  files,
  onCancelFile,
  onOpenChange,
  onRetry,
  onRetryFile,
  open,
}: NarrativeUploadDialogProps) {
  const { t } = useTranslation();
  const completedCount = files.filter((f) => f.status === 'completed').length;
  const failedCount = files.filter((f) => f.status === 'failed').length;
  const totalCount = files.length;
  const progressPercentage = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent className="bg-popover w-[calc(100vw-24px)] max-w-[320px] gap-0 rounded-md border-0 p-0 shadow-lg">
        {/* Header */}
        <DialogHeader className="mb-2 !gap-0 px-3 pt-2 !pb-2">
          <div className="flex items-center">
            <DialogTitle className="text-foreground font-['Inter'] text-[14px] leading-[160%] font-medium tracking-normal">
              {t('profile.narratives.uploadDialog.title')}
            </DialogTitle>
          </div>
        </DialogHeader>

        {/* Divider Above Files */}
        <div className="bg-elevated-hover h-px w-full" />

        {/* Files List */}
        <div className="custom-scrollbar max-h-[300px] overflow-y-auto">
          <div className="flex flex-col px-3">
            {files.map((file, index) => (
              <div key={file.id}>
                <NarrativeUploadFileCard
                  file={file}
                  {...(onRetryFile && { onRetry: onRetryFile })}
                  {...(onCancelFile && { onCancel: onCancelFile })}
                />
                {index < files.length - 1 && <div className="bg-elevated-hover h-px w-full" />}
              </div>
            ))}
          </div>
        </div>

        {/* Footer with Progress Bar as Divider */}
        <div>
          <div className="bg-elevated-hover mx-3 h-[4px] w-auto">
            <div
              className="bg-primary-active h-full transition-all duration-300"
              style={{
                width: `${progressPercentage}%`,
              }}
            />
          </div>
          <div className="mx-3 flex min-h-[42px] items-center justify-between gap-2 px-0 py-1">
            <p className="text-tertiary min-w-0 flex-1 truncate font-['Inter'] text-[12px] leading-[170%] font-normal tracking-normal">
              {failedCount > 0
                ? t('profile.narratives.uploadDialog.statusWithFailed', {
                    completed: completedCount,
                    failed: failedCount,
                    total: totalCount,
                  })
                : t('profile.narratives.uploadDialog.status', {
                    completed: completedCount,
                    total: totalCount,
                  })}
            </p>
            {failedCount > 0 && onRetry && (
              <Button
                className="border-border-strong bg-back-bg2 text-secondary hover:bg-elevated-subtle h-7 px-3 text-xs"
                onClick={onRetry}
                size="sm"
                variant="outline"
              >
                {t('profile.narratives.uploadDialog.retry')}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
